// @flow

import React, {useState, useEffect, useRef} from "react";
import {Col, Table, Form, Input, Switch, Button, Checkbox} from "antd";
import {useFormik, FormikProvider} from "formik";
import {useHistory} from "react-router-dom";
import {useTranslation} from "react-i18next";

import {handleCheckBoxText} from "../../../utils/helpers";

import {ErrorFocus} from "../../_defaultComponents/ErrorFocus/ErrorFocus";
import {
    CompanyContanier,
    CompanyButtonContainer,
    SubCompanyContainer,
    FilterContainer
} from "./optionsStyles";
import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {CityFilter} from "../../_defaultComponents/FilterComponents";
import {EmptyMessage} from "../../_defaultComponents/EmptyMessage/EmptyMessage";

const {Column} = Table;

export const DeliveryVerificationOptions = ({companyId}) => {
    const stores = useStore();
    const history = useHistory();
    const {t} = useTranslation(["deliveryVerificationOptions"]);

    const [currentPage, setCurrentPage] = useState(1);
    const [isCompanyFormChanged, setIsCompanyFormChanged] = useState(false);
    const [companyFormData, setCompanyFormData] = useState();
    const [city, setCity] = useState("all");
    const [searchText, setSearchText] = useState("");
    const [text, setText] = useState("");
    const [showSubmitButton, setShowSubmitButton] = useState({});
    const [isSubCompanySubmitting, setIsSubCompanySubmitting] = useState(false);
    const [companyRecall, setCompanyRecall] = useState(false);
    const [subCompanyRecall, setSubCompanyRecall] = useState(false);

    const companyRecallRef = useRef(companyRecall);
    companyRecallRef.current = companyRecall;
    const subCompanyRecallRef = useRef(subCompanyRecall);
    subCompanyRecallRef.current = subCompanyRecall;

    const [companyLoading, companyData, setCompanyData, setCompanyLoading] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.companyStore.getCompany.bind(null, {
            companyId,
            withSubCompanies: false,
            vehicleType: null
        }),
        dependencies: [companyRecall]
    });

    const [subCompanyLoading, subCompanyData, setSubCompanyData, setSubCompanyLoading] = useFetch({
        defaultDataValue: {},
        fetchFn:
            companyData.data && companyData.data._id
                ? stores.companyStore.getSubCompanyList.bind(null, {
                      companyId: companyData.data._id,
                      limit: 10,
                      skip: (currentPage - 1) * 10,
                      activity: "enabled",
                      vehicleType: null,
                      city,
                      searchText
                  })
                : () => [],
        dependencies: [companyData, currentPage, subCompanyRecall, city, searchText]
    });

    const formik = useFormik({
        initialValues: {
            options: {
                verification: {
                    isActive: companyData?.data?.options?.verification?.isActive,
                    applyVerificationOptions: false
                }
            },
            ...(companyData && companyData.data)
        },
        enableReinitialize: true,
        validateOnChange: true,
        onSubmit: async values => {
            setCompanyLoading(true);

            let filters = {};
            filters.searchText = searchText;
            filters.city = city;
            if (Object.keys(filters).length > 0) {
                values.filters = filters;
            }

            await stores.companyStore.updateCompanyVerificationOptions(values);
            if (values.isActive) {
                setSubCompanyRecall(!subCompanyRecallRef.current);
            }
            setCompanyRecall(!companyRecallRef.current);
            setCompanyLoading(false);
            setIsCompanyFormChanged(false);
        }
    });

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue
    } = formik;

    useEffect(() => {
        if (values && !isCompanyFormChanged) {
            setCompanyFormData(values);
        } else if (values && JSON.stringify(companyFormData) !== JSON.stringify(values)) {
            setIsCompanyFormChanged(true);
        } else {
            setIsCompanyFormChanged(false);
        }
    }, [values]);

    const onSubCompanySubmit = async index => {
        setIsSubCompanySubmitting(true);
        let submitData = subCompanyData.rows[index]?.options?.verification;
        submitData.name = subCompanyData.rows[index]?.name;
        submitData._id = subCompanyData.rows[index]?._id;
        const params = {
            subCompanyId: submitData._id,
            options: {
                isActive: submitData.isActive
            }
        };
        const result = await stores.companyStore.updateSubCompanyVerificationOptions(params);
        if (result) {
            setSubCompanyRecall(!subCompanyRecallRef.current);
        }
        setIsSubCompanySubmitting(false);
    };

    return (
        <div data-testid="deliveryVerificationOptions">
            <h2 data-testid="companyName">{companyData?.data?.companyName}</h2>
            <CompanyContanier>
                <Form
                    autoComplete="off"
                    data-testid="deliveryVerificationForm"
                    onFinish={handleSubmit}
                    onChange={e => setIsCompanyFormChanged(true)}
                >
                    <Table
                        loading={companyLoading}
                        dataSource={[companyData.data]}
                        rowKey="_id"
                        data-testid="deliveryVerificationTable"
                        pagination={false}
                        locale={{
                            emptyText: <EmptyMessage />
                        }}
                    >
                        <ErrorFocus formik={formik} />
                        <FormikProvider value={formik}>
                            <Column
                                title={`${t(
                                    "deliveryVerificationOptions.fields.deliveryVerificationOptions.label",
                                    {ns: "deliveryVerificationOptions"}
                                )} (${t(
                                    "deliveryVerificationOptions.fields.deliveryVerificationOptions.short",
                                    {ns: "deliveryVerificationOptions"}
                                )})`}
                                dataIndex="isActive"
                                render={text => (
                                    <Form.Item>
                                        <Switch
                                            data-testid="deliveryVerificationWitch"
                                            checked={values.options.verification?.isActive}
                                            checkedChildren={t("other.active", {ns: "common"})}
                                            unCheckedChildren={t("other.passive", {ns: "common"})}
                                            onChange={e => {
                                                setFieldValue("options.verification.isActive", e);
                                                setIsCompanyFormChanged(true);
                                            }}
                                        />
                                    </Form.Item>
                                )}
                                align="left"
                            />
                        </FormikProvider>
                    </Table>
                    <CompanyButtonContainer justify="end" align="middle">
                        <Checkbox
                            data-testid="applyForAllSubCompaniesCheckbox"
                            checked={values?.applyVerificationOptions}
                            onChange={e =>
                                setFieldValue("applyVerificationOptions", e.target.checked)
                            }
                        >
                            {handleCheckBoxText(searchText, city)}
                        </Checkbox>
                        <Button
                            htmlType="submit"
                            disabled={!isCompanyFormChanged || isSubmitting}
                            type="primary"
                            data-testid="submitButton"
                        >
                            {t("actions.save", {ns: "common"})}
                        </Button>
                    </CompanyButtonContainer>
                </Form>
            </CompanyContanier>
            <Col span={12}>
                <FilterContainer type="flex" gutter={[20, 20]}>
                    <Col span={6}>
                        <CityFilter
                            value={city}
                            onChange={value => {
                                setCurrentPage(1);
                                setCity(value);
                            }}
                        />
                    </Col>
                    <Col span={6}>
                        <h5>{t("filters.search", {ns: "common"})}</h5>
                        <Input.Search
                            data-testid="searchInput"
                            value={text}
                            onSearch={value => {
                                setCurrentPage(1);
                                setSearchText(value);
                            }}
                            onChange={event => {
                                setText(event.target.value);
                            }}
                        />
                    </Col>
                </FilterContainer>
            </Col>
            <SubCompanyContainer>
                <Table
                    rowKey="_id"
                    data-testid="subCompaniesTable"
                    dataSource={subCompanyData?.rows}
                    loading={subCompanyLoading}
                    pagination={{
                        total: subCompanyData.dataCount,
                        current: currentPage,
                        onChange: async page => {
                            setCurrentPage(page);
                            history.push(
                                "/companies/delivery-verification-options/" +
                                    companyId +
                                    "?page=" +
                                    page
                            );
                        },
                        hideOnSinglePage: true,
                        showSizeChanger: false
                    }}
                    locale={{
                        emptyText: <EmptyMessage />
                    }}
                >
                    <Column
                        dataIndex="name"
                        title={t("fields.subcompany", {ns: "common"})}
                        render={text => <span data-testid="name">{text}</span>}
                    />
                    <Column
                        dataIndex="city"
                        title={t("fields.city", {ns: "common"})}
                        render={text => <span data-testid="city">{text}</span>}
                    />
                    <Column
                        dataIndex="isActive"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title={t(
                            "deliveryVerificationOptions.fields.deliveryVerificationOptions.short",
                            {ns: "deliveryVerificationOptions"}
                        )}
                        render={(text, record, index) => {
                            return (
                                <Switch
                                    data-testid="deliveryVerificationSwitch"
                                    checked={
                                        subCompanyData.rows[index]?.options?.verification?.isActive
                                    }
                                    onChange={e => {
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            const options = {isActive: e};
                                            newItem.options.verification = options;
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="_id"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        render={(text, record, index) =>
                            showSubmitButton[text] && (
                                <Button
                                    disabled={isSubCompanySubmitting}
                                    onClick={() => onSubCompanySubmit(index)}
                                    type="primary"
                                    data-testid="submitButton"
                                >
                                    {t("actions.save", {ns: "common"})}
                                </Button>
                            )
                        }
                    />
                </Table>
            </SubCompanyContainer>
        </div>
    );
};
